// src/components/user/AccountReturnModal.jsx
// The way back for a member who is closing their account (members-can-leave addenda 3–4, Roadmap
// #82). Checked whenever a session starts — App.js mounts this with the signed-in member's id, so
// a sign-in, and a page opened with a session already stored, both reach it.
//
// While a member is closing, everything they published and their profile are hidden, and the
// database refuses their writes. So this window blocks (LAYOUT.md § Modal Shell: no ESC, no
// outside click): the only ways out are Come back, or signing out.
//
// A failed status check shows nothing (it is logged): every signed-in member runs this check, and
// an error must not reach members who are not closing (Advisor SA-4). An error is shown only after
// the member has pressed Come back.

import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from '../core/Modal';
import StandardButton from '../core/StandardButton';
import {
  getAccountClosureStatus,
  cancelAccountClosure,
  closureRefusal,
  formatClosureDeadline,
} from '../../data/accountService';

// 'hidden' | 'offer' | 'returning' | 'failed' | 'too-late' | 'returned'
const AccountReturnModal = ({ userId, onSignOut }) => {
  const { t, formattingLocale } = useTranslation('user');
  const [state, setState] = useState('hidden');
  const [deleteAfter, setDeleteAfter] = useState(null);

  useEffect(() => {
    if (!userId) {
      setState('hidden');
      return undefined;
    }
    let cancelled = false;
    getAccountClosureStatus().then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        console.warn('Account closure status could not be read:', error);
        return;
      }
      if (!data.closing) {
        setState('hidden');
        return;
      }
      setDeleteAfter(data.delete_after);
      setState(data.can_return ? 'offer' : 'too-late');
    });
    return () => { cancelled = true; };
  }, [userId]);

  const handleComeBack = async () => {
    setState('returning');
    const { error } = await cancelAccountClosure();
    if (error) {
      console.error('Coming back failed:', error);
      const refusal = closureRefusal(error);
      if (refusal.reason === 'too_late') setState('too-late');
      else if (refusal.reason === 'not_closing') setState('returned');
      else setState('failed');
      return;
    }
    setState('returned');
  };

  // After a return, reload so every view reads the restored content and profile afresh.
  const handleReturnedDone = () => {
    window.location.reload();
  };

  if (state === 'hidden') return null;

  const date = deleteAfter ? formatClosureDeadline(deleteAfter, formattingLocale) : '';
  const busy = state === 'returning';

  return (
    <Modal
      isOpen
      onClose={() => {}}
      closeOnBackdrop={false}
      closeOnEsc={false}
      panelClassName="bg-white rounded-lg p-8 max-w-md mx-4"
    >
      {state === 'returned' ? (
        <>
          <h3 className="text-xl font-bold mb-4">{t('return_done_title')}</h3>
          <p className="text-gray-700 mb-6">{t('return_done_body')}</p>
          <div className="flex justify-end">
            <StandardButton variant="action" label={t('return_button_done')} onClick={handleReturnedDone} />
          </div>
        </>
      ) : state === 'too-late' ? (
        <>
          <h3 className="text-xl font-bold mb-4">{t('return_title')}</h3>
          <p className="text-gray-700 mb-6">{t('return_too_late')}</p>
          <div className="flex justify-end">
            <StandardButton variant="utility" label={t('return_button_sign_out')} onClick={onSignOut} />
          </div>
        </>
      ) : (
        <>
          <h3 className="text-xl font-bold mb-4">{t('return_title')}</h3>
          <p className="text-gray-700 mb-3">{t('return_body', { date })}</p>
          <p className="text-gray-700 mb-6">{t('return_question')}</p>
          {state === 'failed' && (
            <p role="alert" className="text-red-600 mb-4">{t('return_failed')}</p>
          )}
          <div className="flex gap-4 justify-end">
            <StandardButton
              variant="utility"
              label={t('return_button_keep_closing')}
              disabled={busy}
              onClick={onSignOut}
            />
            <StandardButton
              variant="action"
              label={busy ? t('return_button_coming_back') : t('return_button_come_back')}
              disabled={busy}
              onClick={handleComeBack}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default AccountReturnModal;
